import { useCallback } from 'react';

/**
 * Helpers to mutate company edit item lists, like contacts & locations
 */
export const useEditList = <T extends { isHidden?: boolean; isDeleted?: boolean }>({
  list,
  keyColumn,
  emptyItem,
  removeConfirmationText,
  setList,
}: {
  list: T[];
  keyColumn: keyof T;
  emptyItem: T;
  removeConfirmationText: string;
  setList: (list: T[]) => void;
}) => {
  const move = useCallback(
    (id: number, direction: number) => {
      const deletedItems = list.filter(({ isDeleted }) => isDeleted);
      const existingItems = list.filter(({ isDeleted }) => !isDeleted);

      const one = existingItems.findIndex((item) => (item as any)[keyColumn] === id);
      const two = one - direction;

      if (two >= existingItems.length || two < 0) return;

      // Swap array items
      const ul = [...existingItems];
      [ul[one], ul[two]] = [ul[two], ul[one]];

      return setList([...ul, ...deletedItems]);
    },
    [list]
  );

  const remove = useCallback(
    (id: number) => {
      const isConfirmed = window.confirm(removeConfirmationText);
      if (!isConfirmed) return;

      const index = list.findIndex((item) => (item as any)[keyColumn] === id);

      // Remove item
      const ul = [...list];
      ul[index].isDeleted = true;

      return setList(ul);
    },
    [removeConfirmationText, list]
  );

  const create = useCallback(() => {
    const item = { ...emptyItem, [keyColumn]: -list.length };
    setList([...list, item]);
    return item;
  }, [emptyItem, list]);

  const toggleIsHidden = useCallback(
    (id: number) => {
      const newList = [...list];
      const index = list.findIndex((item) => (item as any)[keyColumn] === id);
      newList[index].isHidden = !newList[index].isHidden;
      return setList(newList);
    },
    [list]
  );

  return { toggleIsHidden, move, remove, create };
};
